/*
 * Schedule
 *
 * Top-level schedule component that renders a weekly grid of classes.
 *
 * Responsibilities:
 * - Fetches schedule data from the backend (expected shape described in
 *   scheduleUtils.buildScheduleGrid). The raw backend payload is an array
 *   where each item is a single-key object: { "Class Name": { start_time, end_time, days, level, class_type } }
 * - Transforms the backend payload into a renderable grid via buildScheduleGrid().
 * - Manages UI filters (type/level) and passes them down to ScheduleCell.
 * - Computes visual sizing (hours -> pixel height) using SLOT_HEIGHT and
 *   getGridHours(). All vertical positioning is computed relative to SLOT_HEIGHT.
 *
 * Important notes / assumptions:
 * - Do not rely on any positional CSS outside this component, it computes
 *   absolute positions for class blocks inside each day column.
 */

import React, { useEffect, useState } from 'react';
import api from '../api';
import { Box, Typography, Grid, CircularProgress } from '@mui/material';
import FilterBar from './FilterBar';
import ScheduleCell from './ScheduleCell';
import useMediaQuery from '@mui/material/useMediaQuery';
import { buildScheduleGrid, getGridHours } from './scheduleUtils';


const days = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

// Height in pixels of one hour slot in the visual grid. All vertical
// positioning of class blocks is computed relative to this value.
const SLOT_HEIGHT = 90;

function Schedule() {
  const [rawData, setRawData] = useState([]);
  // const [gridData, setGridData] = useState({});
  const [filters, setFilters] = useState({ type: 'ST', level: 'ALL' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    //axios.get(${config.API_URL}/schedule/)
    //axios.get('http://localhost:8000/schedule/')
    api.get('/schedule/')
      .then(res => {
        setRawData(res.data);
        // setGridData(buildScheduleGrid(res.data));
        setLoading(false);
      });
  }, []);

  // 'hours' is an array of formatted hour labels (['12 PM', '1 PM', ...'])computed from the raw backend data
  // 'totalHeight' defines the minimum vertical space needed to show all hour slots in a column.

  const filtered = rawData.filter(entry => {
  const name = Object.keys(entry)[0];
  const data = entry[name];
  return data.class_type.includes(filters.type);
  });

  const hours = getGridHours(filtered);
  const earliestHour = filtered.length
  ? Math.min(
      ...filtered.map(entry => {
        const values = Object.values(entry)[0];
        return Number(values.start_time.split(":")[0]);
      })
    )
  : 12;

  const filteredGrid = buildScheduleGrid(filtered, earliestHour);

  const totalHeight = hours.length * SLOT_HEIGHT;
  const isMobile = useMediaQuery('(max-width:600px)');
  const isSmall = useMediaQuery('(max-width:900px)');
  const isMedium = useMediaQuery('(max-width:1200px)');
  const abbreviateName = isMobile || isSmall || isMedium; // abbreviate on mobile, small and medium screens

  if (loading) return (
    <Box sx={{ mt: 8, textAlign: 'center' }}>
      <CircularProgress color="inherit" />
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 1, sm: 4 }, py: 6, backgroundColor: { xs: '#1E1E1E', md: '#111' }, color: '#fff' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        Weekly Schedule
      </Typography>

  {/* FilterBar controls the 'filters' state used by ScheduleCell to hide/show entries */}
  <FilterBar filters={filters} setFilters={setFilters} />

      {/* Day Labels */}
      <Grid container sx={{ mb: 1 }}>
        {!isMobile && <Grid item xs={1} />}
        {days.map(day => (
          <Grid item xs key={day}>
            <Typography variant="subtitle2" align="center" sx={{ fontWeight: 'bold' }}>
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Schedule Grid */}
      <Grid container>
        {/* Time column (left-most): shows the hour labels down the page. Hidden on mobile so day columns expand. */}
        {!isMobile && (
          <Grid item xs={1}>
            {hours.map((hour, i) => (
              <Box key={i} sx={{ height: SLOT_HEIGHT, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">{hour}</Typography>
              </Box>
            ))}
          </Grid>
        )}

        {/* Day columns: each column contains a stacked visual grid and class blocks */}
        {days.map((day, dayIndex) => (
          <Grid item xs key={day}>
            <Box sx={{
              borderLeft: '1px solid #444',
              position: 'relative',
              minHeight: totalHeight,
              backgroundColor: dayIndex % 2 === 0 ? '#111' : '#1a1a1a'
            }}>
              {/* Hour dividers */}
              {hours.map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    top: `${i * SLOT_HEIGHT}px`,
                    width: '100%',
                    height: '1px',
                    backgroundColor: '#333'
                  }}
                />
              ))}

              {/* Final divider after last slot */}
              <Box
                sx={{
                  position: 'absolute',
                  top: `${totalHeight}px`,
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#333'
                }}
              />

              {/* Class blocks: render a ScheduleCell for each entry on this day.
                  'overlappingEntries' array so eachScheduleCell can decide how to
                  render when multiple classes overlap in time. */}
              {filteredGrid[day]?.map((entry, idx, arr) => {
                const overlappingEntries = arr.filter(
                  e =>
                    // Overlap if times intersect
                    (e.start < entry.end && e.end > entry.start)
                );
                return (
                  <ScheduleCell
                    key={entry.id || idx}
                    entry={entry}
                    filters={filters}
                    slotHeight={SLOT_HEIGHT}
                    isMobile={isMobile}
                    isSmall={isSmall}
                    abbreviateName={abbreviateName}
                    overlappingEntries={overlappingEntries}
                  />
                );
              })}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default Schedule;
